import { createProject } from '../../../hooks/useProjects';
import { directSupabase } from '../../../lib/supabaseClient';
import { customerService } from '../../../services/customerService';
import { createDocumentShareLink } from '../services/documentShareLinks.service';
import { documentsService } from '../services/documents.service';
import { visualDesignerSurveysService } from '../services/visualDesignerSurveys.service';
import { computeInvoiceDelta, normalizeInvoiceRole } from '../utils/invoiceLedger';
import {
  HARNESS_DEPOSIT_GROSS,
  HARNESS_DEPOSIT_PCT,
  HARNESS_LINE_NET,
  HARNESS_QUOTE_GROSS,
  HARNESS_VAT_PCT,
  assertStep,
} from './vdScenario.expectations';
import {
  VD_HARNESS_NOTE,
  VD_HARNESS_PREFIX,
  type VdScenarioFixture,
  type VdScenarioId,
  type VdScenarioStep,
} from './vdScenario.types';

function runId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function isHarnessName(value?: string | null): boolean {
  return String(value || '').toUpperCase().startsWith(VD_HARNESS_PREFIX);
}

async function resolveKsgOrganisation(): Promise<{ id: string; name: string }> {
  const orgs = await documentsService.listOrganisations();
  const ksg = orgs.find((org) => String(org.code || '').toUpperCase() === 'KSG') || orgs[0];
  if (!ksg) throw new Error('No organisation available for a harness run.');
  return ksg;
}

async function setQuoteLineNet(quoteId: string, unitPrice: number): Promise<void> {
  const quote = await documentsService.getDocument(quoteId);
  if (!quote) throw new Error('Harness quote disappeared.');
  const lines = (quote.lines || []).map((line, index) => ({
    lineKind: line.lineKind,
    additionalType: line.additionalType,
    name: line.name,
    description: line.description,
    qty: line.qty,
    unit: line.unit,
    unitPrice: index === 0 ? unitPrice : line.unitPrice,
    vatInclusive: line.vatInclusive,
    sortOrder: line.sortOrder,
    metaJson: line.metaJson,
  }));
  await documentsService.replaceLines(quoteId, lines);
}

export async function seedHarnessWorkspace(): Promise<VdScenarioFixture> {
  const id = runId();
  const org = await resolveKsgOrganisation();
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const customer = await customerService.createLocalCustomer({
    title: null,
    first_name: VD_HARNESS_PREFIX,
    last_name: id,
    name: `${VD_HARNESS_PREFIX} ${id}`,
    phone_number: `07000${String(Date.now()).slice(-6)}`,
    email: `vdtest+${id}@example.invalid`,
    address: '1 Harness Lane, Test',
    organization_id: 4,
    organization_name: 'Keystone Glazing',
  });

  const created = await createProject({
    project_name: `${VD_HARNESS_PREFIX} ${stamp}`,
    customer_id: customer.id,
    customer_name: customer.name,
    customer_email: customer.email || '',
    notes: VD_HARNESS_NOTE,
    deposit_received: false,
    ordered: false,
    product_types: ['Windows'],
    supplier_quote_received: false,
    supplier_prices: [],
    complete: false,
    workflow_tasks: [],
    status: 'pending',
    preferred_suppliers: [],
    onedrive_folder_id: null,
    supply_only: false,
    lifecycle_ref: null,
  });
  if (!created.success || !created.data?.id) {
    throw new Error('Failed to create a throwaway harness workspace.');
  }
  const projectId = String(created.data.id);
  const lifecycleRef = String(created.data.lifecycle_ref || '').trim()
    || await documentsService.mintLifecycleRef(org.id);

  const quote = await documentsService.createDocument({
    kind: 'Quotation',
    lifecycleRef,
    organisationId: org.id,
    isOriginal: true,
    projectId,
    customerId: customer.id,
    customerFirstName: VD_HARNESS_PREFIX,
    customerLastName: id,
    title: `${VD_HARNESS_PREFIX} casement`,
    description: VD_HARNESS_NOTE,
    status: 'draft',
    currency: 'GBP',
    hasDeposit: true,
    depositPercentage: HARNESS_DEPOSIT_PCT,
    depositAmount: HARNESS_DEPOSIT_GROSS,
    vatEnabled: true,
    vatPct: HARNESS_VAT_PCT,
    projectDays: 1,
    fees: { adminPct: 0, adminCap: 0, contingencyPct: 0, profitPct: 0, developerPct: null },
    source: 'jarvis',
  }, [{
    lineKind: 'other',
    additionalType: 'other',
    name: 'VDTEST casement',
    description: 'Synthetic Visual Designer line for the scenario engine.',
    qty: 1,
    unit: 'item',
    unitPrice: HARNESS_LINE_NET,
    vatInclusive: false,
    sortOrder: 0,
    metaJson: {
      feature: 'visual_designer',
      specs: { width_mm: 1200, height_mm: 1400, product: 'casement' },
    },
  }]);

  await visualDesignerSurveysService.ensureFirstSurveyBoundToQuote(projectId, quote.id);
  const versionId = String(quote.activeVersionId || '').trim();
  const share = versionId
    ? await createDocumentShareLink(quote.id, {
      sentVersionId: versionId,
      sourceVersionId: versionId,
      surveyArtefact: 'first',
      sentGrossTotal: Number(quote.grossTotal || HARNESS_QUOTE_GROSS),
    })
    : null;
  if (versionId) {
    await directSupabase
      .from('document_versions')
      .update({
        is_transmitted: true,
        transmitted_at: new Date().toISOString(),
      })
      .eq('id', versionId);
  }
  await documentsService.updateDocument(quote.id, { status: 'sent' });

  return {
    runId: id,
    customerId: customer.id,
    projectId,
    quoteId: quote.id,
    lifecycleRef: quote.lifecycleRef,
    organisationId: org.id,
    quotedGross: Number(quote.grossTotal || HARNESS_QUOTE_GROSS),
    depositGross: Number(quote.depositAmount || HARNESS_DEPOSIT_GROSS),
    shareLinkId: share?.id || null,
    shareToken: share?.token || null,
  };
}

export async function mockCustomerAccept(quoteId: string): Promise<void> {
  await documentsService.updateDocument(quoteId, { status: 'accepted' });
  const quote = await documentsService.getDocument(quoteId);
  if (!quote) throw new Error('Quote missing after mock accept.');
  await documentsService.ensureDepositInvoice(quote.id, quote.activeVersionId);
}

export async function mockCustomerReject(quoteId: string): Promise<void> {
  await documentsService.updateDocument(quoteId, { status: 'rejected' });
}

export async function purgeHarnessFixture(fixture: Pick<VdScenarioFixture, 'projectId' | 'customerId'>): Promise<void> {
  const { data: project } = await directSupabase
    .from('projects')
    .select('id, project_name, notes')
    .eq('id', fixture.projectId)
    .maybeSingle();
  if (!project || (!isHarnessName(project.project_name) && !String(project.notes || '').includes(VD_HARNESS_PREFIX))) {
    throw new Error('Refusing to purge a workspace that was not created by the VDTEST harness.');
  }
  const { error } = await directSupabase.rpc('purge_workspace', { p_project_id: fixture.projectId });
  if (error) throw new Error(error.message);
  const { data: customer } = await directSupabase
    .from('customers')
    .select('id, name')
    .eq('id', fixture.customerId)
    .maybeSingle();
  if (customer && isHarnessName(customer.name)) {
    await customerService.deleteLocalCustomer(String(customer.id));
  }
}

async function ledgerForQuote(quoteId: string, settledContract?: number) {
  const quote = await documentsService.getDocument(quoteId);
  if (!quote) throw new Error('Quote not found.');
  const invoices = await documentsService.listLifecycleInvoices(quote.organisationId, quote.lifecycleRef);
  return {
    quote,
    invoices,
    ledger: computeInvoiceDelta({
      settledContract: settledContract != null ? settledContract : Number(quote.grossTotal || 0),
      invoices: invoices.map((invoice) => ({
        role: invoice.invoiceRole,
        status: invoice.status,
        grossTotal: invoice.grossTotal,
        depositAmount: invoice.depositAmount,
      })),
    }),
  };
}

export async function runVdScenario(
  scenarioId: VdScenarioId,
  options?: { purgeAfter?: boolean },
): Promise<{ fixture: VdScenarioFixture; steps: VdScenarioStep[] }> {
  const fixture = await seedHarnessWorkspace();
  const steps: VdScenarioStep[] = [
    assertStep('seed', 'Created throwaway VDTEST workspace', true, fixture.lifecycleRef),
  ];

  try {
    if (scenarioId === 'reject-creates-no-invoice') {
      await mockCustomerReject(fixture.quoteId);
      const { invoices } = await ledgerForQuote(fixture.quoteId);
      steps.push(assertStep('reject', 'Mocked customer reject', true));
      steps.push(assertStep(
        'no-invoice',
        'No deposit invoice was created',
        invoices.filter((invoice) => String(invoice.status || '').toLowerCase() !== 'void').length === 0,
        `${invoices.length} invoice(s)`,
      ));
    } else {
      await mockCustomerAccept(fixture.quoteId);
      const afterAccept = await ledgerForQuote(fixture.quoteId);
      const deposit = afterAccept.invoices.find((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'deposit');
      steps.push(assertStep('accept', 'Mocked customer accept', true));
      steps.push(assertStep(
        'deposit',
        'Deposit invoice matches the nominated 40%',
        !!deposit && Math.abs(Number(deposit.grossTotal || 0) - HARNESS_DEPOSIT_GROSS) < 0.02,
        deposit ? `£${Number(deposit.grossTotal || 0).toFixed(2)}` : 'missing',
      ));
      steps.push(assertStep(
        'deposit-only',
        'Only the deposit is invoiced so far',
        afterAccept.ledger.nextRole === 'works' && afterAccept.ledger.delta === HARNESS_QUOTE_GROSS - HARNESS_DEPOSIT_GROSS,
        `next=${afterAccept.ledger.nextRole || 'none'} delta=${afterAccept.ledger.delta}`,
      ));

      if (scenarioId === 'final-increase-before-balance-is-works') {
        await setQuoteLineNet(fixture.quoteId, 1100);
        const raised = await ledgerForQuote(fixture.quoteId);
        const settled = Number(raised.quote.grossTotal || 0);
        const created = await documentsService.issueBalanceInvoice(fixture.quoteId, { settledContract: settled });
        const afterIssue = await ledgerForQuote(fixture.quoteId, settled);
        const works = afterIssue.invoices.find((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'works');
        steps.push(assertStep(
          'works-includes-increase',
          'First leftover after a Final increase is a balance, not a variation',
          normalizeInvoiceRole(created?.invoiceRole) === 'works'
            && Math.abs(Number(works?.grossTotal || 0) - (settled - HARNESS_DEPOSIT_GROSS)) < 0.02,
          `settled £${settled.toFixed(2)} balance £${Number(works?.grossTotal || 0).toFixed(2)}`,
        ));
        steps.push(assertStep(
          'not-variation',
          'No variation invoice was raised',
          !afterIssue.invoices.some((invoice) => normalizeInvoiceRole(invoice.invoiceRole) === 'variation'),
        ));
      }

      if (scenarioId === 'increase-after-balance-is-variation') {
        const firstBalance = await documentsService.issueBalanceInvoice(fixture.quoteId, {
          settledContract: HARNESS_QUOTE_GROSS,
        });
        steps.push(assertStep(
          'balance',
          'Issued the original outstanding as a balance',
          normalizeInvoiceRole(firstBalance?.invoiceRole) === 'works'
            && Math.abs(Number(firstBalance?.grossTotal || 0) - (HARNESS_QUOTE_GROSS - HARNESS_DEPOSIT_GROSS)) < 0.02,
          firstBalance ? `£${Number(firstBalance.grossTotal || 0).toFixed(2)}` : 'missing',
        ));
        await setQuoteLineNet(fixture.quoteId, 1100);
        const raised = await documentsService.getDocument(fixture.quoteId);
        const settled = Number(raised?.grossTotal || 0);
        const variation = await documentsService.issueBalanceInvoice(fixture.quoteId, { settledContract: settled });
        steps.push(assertStep(
          'variation',
          'A later increase is a variation only',
          normalizeInvoiceRole(variation?.invoiceRole) === 'variation'
            && Math.abs(Number(variation?.grossTotal || 0) - (settled - HARNESS_QUOTE_GROSS)) < 0.02,
          variation ? `£${Number(variation.grossTotal || 0).toFixed(2)}` : 'missing',
        ));
      }
    }
  } catch (error) {
    steps.push(assertStep(
      'run',
      'Scenario finished',
      false,
      error instanceof Error ? error.message : String(error),
    ));
  }

  if (options?.purgeAfter) {
    try {
      await purgeHarnessFixture(fixture);
      steps.push(assertStep('purge', 'Purged the throwaway workspace', true));
    } catch (error) {
      steps.push(assertStep(
        'purge',
        'Purged the throwaway workspace',
        false,
        error instanceof Error ? error.message : String(error),
      ));
    }
  }

  return { fixture, steps };
}
